<div 
  class="page min-h-screen bg-white font-sans text-gray-900 selection:bg-orange-100 selection:text-brand flex flex-col relative overflow-hidden"
  data-testid="wizard-school-section"
>

  <!-- Background Decor -->
  <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
  <div class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 pointer-events-none -translate-x-1/3 translate-y-1/3"></div>

  <!-- Header / Logo -->
  <div class="w-full max-w-5xl mx-auto px-6 py-8 relative z-10">
    <a routerLink="/wizard/v2/landing" class="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="wizard-school-logo">
      <img src="images/logo.png" alt="JustTeach Logo" class="h-8 w-auto"/>
    </a>
  </div>

  <!-- Main Content -->
  <div class="flex-1 flex items-center justify-center px-4 pb-12 relative z-10">
    <div class="max-w-2xl w-full">

      <!-- Stepper -->
      <div class="mb-12" data-testid="wizard-school-stepper">
        <div class="flex items-start justify-center space-x-4">
          <!-- Step 1: Completed -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-school-stepper-step-0">
            <div class="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm shadow-md">
              <lucide-icon [img]="icons.Check" class="w-5 h-5"></lucide-icon>
            </div>
            <span class="font-bold text-sm text-green-600">Profile</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-brand rounded-full mt-4"></div>

          <!-- Step 2: Active -->
          <div class="flex flex-col items-center gap-2" data-testid="wizard-school-stepper-step-1">
            <div class="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-bold text-sm shadow-lg shadow-brand/20">
              2
            </div>
            <span class="font-bold text-sm text-brand">School</span>
          </div>

          <!-- Connector Line -->
          <div class="w-16 h-0.5 bg-gray-200 rounded-full mt-4"></div>

          <!-- Step 3: Pending -->
          <div class="flex flex-col items-center gap-2 opacity-50" data-testid="wizard-school-stepper-step-2">
            <div class="w-8 h-8 rounded-full bg-gray-100 text-gray-500 border border-gray-200 flex items-center justify-center font-bold text-sm">
              3
            </div>
            <span class="font-medium text-sm text-gray-500">Personalize</span>
          </div>
        </div>
      </div>

      <div class="text-center mb-10">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4" data-testid="wizard-school-heading">
          Where do you teach?
        </h1>
        <p class="text-gray-600 text-lg max-w-lg mx-auto leading-relaxed" data-testid="wizard-school-subheading">
          Tell us about your school so we can align content with your institution's standards.
        </p>
      </div>

      <div class="bg-white/80 backdrop-blur-xl border border-white shadow-2xl shadow-gray-200/50 rounded-2xl p-6 sm:p-10" data-testid="wizard-school-form-container">
        <form [formGroup]="schoolForm" (ngSubmit)="onSubmit()" class="space-y-8" data-testid="wizard-school-form">

          <!-- School Section -->
          <div class="space-y-3" data-testid="wizard-school-name-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-school-name-label">
              Tell us your school
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <lucide-icon [img]="icons.School" class="w-5 h-5"></lucide-icon>
              </div>
              <input
                type="text"
                formControlName="school"
                class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium placeholder:text-gray-400"
                placeholder="Type your school name, district, or city"
                data-testid="wizard-school-name-input"
              />
            </div>
            <button
              type="button"
              (click)="setNoSchool()"
              class="text-sm font-medium text-brand hover:text-brand-dark hover:underline transition-colors flex items-center gap-1 cursor-pointer"
              data-testid="wizard-school-no-school-btn"
            >
              I don't have a school
            </button>
          </div>

          <!-- District & State -->
          <div class="space-y-3" data-testid="wizard-school-location-section">
            <label class="block text-sm font-bold text-gray-900" data-testid="wizard-school-location-label">
              What district and state are you in?
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div data-testid="wizard-school-district-field">
                <input 
                  type="text"
                  formControlName="district"
                  class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  placeholder="District"
                  data-testid="wizard-school-district-input"
                />
              </div>
              <div data-testid="wizard-school-state-field">
                <input 
                  type="text"
                  formControlName="state"
                  class="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  placeholder="State"
                  data-testid="wizard-school-state-input"
                />
              </div>
            </div>
          </div>

          <div class="pt-4">
            <button
              type="submit"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 group text-lg disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              [disabled]="isLoading$ | async"
              data-testid="wizard-school-submit-btn"
            >
              @if (isLoading$ | async) {
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              } @else {
                Continue
                <lucide-icon [img]="icons.ArrowRight" class="w-5 h-5 group-hover:translate-x-1 transition-transform"></lucide-icon>
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  </div>
</div>
